import { useHomePageRevamp } from '../use-is-home-page';
import { DiscoverProfessionalsSectionLegacy } from './DiscoverProfessionalsSection.legacy';
import { useLoaderData } from 'react-router';
import type { loader as homeLoader } from '~/routes/_app._index';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
} from '@rocco/components/carousel';
import { useCarousel } from '@rocco/components/carousel/carousel';
import { RoccoLink } from '@rocco/components/link';
import { Image } from '@rocco/components/image';
import { GetMatchedHost } from '~/modules-rocco/get-matched/GetMatchedHost';
import { useGetMatchedPromptImpression } from '~/modules-rocco/get-matched/use-prompt-impression';
import { useOpenGetMatchedModal } from '~/state/ui-state-get-matched-modal';
import { useFeature } from '~/modules/root';
import { DiscoverHeader } from '../components/DiscoverHeader';
import { CarouselPaginationPill } from '../components/CarouselPaginationPill';
import { CarouselBrowseMoreTile } from '../components/CarouselBrowseMoreTile';
import { ScrollReveal } from '../components/ScrollReveal';
import { button } from '~/ui-rocco/button-system';
import { useState } from 'react';
import { useAtomValue } from 'jotai';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import type { CarouselApi } from '@rocco/components/carousel';
import { useIslandAutoplay } from '~/ui-rocco/pages/detail-shared/magic-island';
import type { ProfessionalTileViewModel } from '@rocco/ui/tile-professional/view-models/professional';
import { SplitHeading } from '../components/SplitHeading';
import { pinnedFirst } from '~/modules-rocco/home/data-mapper/pinned-first';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@rocco/utils/cn';
import { HOME_MOBILE_BLEED } from '../mobile-carousel-bleed';

/**
 * Discover Professionals (Figma `997:33934` + `997:34729`, white section).
 *
 * The light counterpart of Discover Projects: shared editorial header, a
 * carousel of live featured-professional portrait cards (`professionalSection
 * .featured`), then a "Get Matched" prompt + outline button.
 */
const ProfessionalsPagination = () => {
    const {
        api,
        currentSlide,
        totalSlides,
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
    } = useCarousel();

    // Hide pagination when every slide already fits (nothing to scroll).
    if (!canScrollPrev && !canScrollNext) return null;

    return (
        <div className="flex justify-center max-md:pt-8 md:pt-[2.5vw] lg:pt-[3rem]">
            <CarouselPaginationPill
                total={totalSlides}
                activeIndex={currentSlide}
                onPrev={scrollPrev}
                onNext={scrollNext}
                onSelect={(index) => api?.scrollTo(index)}
                label="Professionals carousel"
            />
        </div>
    );
};

/** What a professional card draws, whichever list it came from. */
interface ProfessionalCard {
    id: number;
    title: string;
    link: string;
    image?: string;
    logo: { src: string; background: string } | null;
    category?: string;
}

const architectCard = (model: ProfessionalTileViewModel): ProfessionalCard => ({
    id: model.id,
    title: model.title,
    link: model.link,
    image: model.image?.src,
    logo: model.logo?.image?.src
        ? { src: model.logo.image.src, background: model.logo.background }
        : null,
    category: model.category,
});

/** The carousel's usual length; pins beyond it still all show. */
const CAROUSEL_CARD_COUNT = 9;

interface DiscoverProfessionalsSectionProps {
    /** Override the eyebrow (e.g. logged-in "Recently viewed"). */
    eyebrow?: string;
    /** Override the display heading (e.g. logged-in "Recently viewed Professionals"). */
    heading?: ReactNode;
}

const DiscoverProfessionalsSectionRevamp = ({
    eyebrow = '(02 – Professionals)',
    heading = <SplitHeading first="Connect with" second="Professionals" />,
}: DiscoverProfessionalsSectionProps = {}) => {
    const {
        professionalSection,
        homeCopy,
        professionalPopularCategories,
        getMatchedStyleImages,
    } = useLoaderData<typeof homeLoader>();
    // The loader merges pinned + trending and applies the size limit; every
    // pinned professional renders even when there are more than nine.
    const featured = professionalSection?.featured ?? [];
    // Architects, as their category page lists them; the curated list (or a
    // member's recently viewed professionals) when there are none.
    const architects =
        professionalSection && 'architects' in professionalSection
            ? professionalSection.architects
            : [];
    // CMS-pinned professionals lead the architects, and all of them show.
    const pinned =
        professionalSection && 'pinned' in professionalSection
            ? professionalSection.pinned
            : [];
    const featuredCard = (
        pro: (typeof featured)[number],
    ): ProfessionalCard => ({
        id: pro.id,
        title: pro.title,
        link: pro.link,
        image: pro.tile?.images?.[0],
        logo: pro.tile?.logo?.image
            ? {
                  src: pro.tile.logo.image,
                  background:
                      pro.tile.logo.variables?.avatarImageBackgroundColor ||
                      'white',
              }
            : null,
        category: pro.tile?.category,
    });
    const cards: ProfessionalCard[] = architects.length
        ? pinnedFirst(
              pinned.map(featuredCard),
              architects.map(architectCard),
              (card) => card.id,
              CAROUSEL_CARD_COUNT,
          )
        : featured.map(featuredCard);

    // On desktop the carousel's pagination and autoplay live in the Magic
    // Island, as the other homepage carousels' do.
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    const islandPagination = !isMobile;
    const [emblaApi, setEmblaApi] = useState<CarouselApi | null>(null);
    const islandRef = useIslandAutoplay<HTMLElement>(emblaApi, {
        label: 'Professionals carousel',
        enabled: islandPagination && cards.length > 1,
    });
    const openGetMatchedModal = useOpenGetMatchedModal('HomeDiscover');
    // Desktop-only by construction: the block below is `hidden` under md, so
    // this prompt has no mobile impressions to report and its open rate is a
    // desktop figure.
    const promptRef =
        useGetMatchedPromptImpression<HTMLParagraphElement>('HomeDiscover');
    const getMatchedEnabled = useFeature('revamp_2026');

    return (
        <section className="w-full overflow-hidden bg-white text-[#181818] md:bg-[#FDF7EE]">
            <DiscoverHeader
                theme="light"
                eyebrow={eyebrow}
                // The projects header's layout: the rule and eyebrow at half
                // width on the right, and the same space around the heading.
                className="md:gap-[5.8333vw] lg:gap-[7rem]"
                headingGapClassName="md:gap-[5.8333vw] lg:gap-[7rem]"
                heading={heading}
                categories={professionalPopularCategories}
                description={homeCopy.professionalsDescription}
                ctaLabel="View all Professionals"
                ctaTo="/professionals"
            />

            {/* Professional cards — full-bleed, centre-aligned so partial
                tiles peek at both edges and the loop reads as endless */}
            <ScrollReveal
                delay={120}
                className="max-md:px-6 max-md:pt-8 md:pt-[8.33vw] lg:pt-[9.996rem]"
            >
                {/* The island follows the carousel, not the header above it. */}
                <div ref={islandPagination ? islandRef : undefined}>
                    <Carousel
                        setApi={setEmblaApi}
                        autoPlay={
                            islandPagination ? undefined : { delay: 4000 }
                        }
                        options={{
                            align: 'start',
                            dragFree: true,
                            duration: 35,
                            containScroll: 'trimSnaps',
                            // Wrap around at either end. Embla falls back to
                            // non-loop automatically when slides don't fill the view.
                            loop: true,
                        }}
                    >
                        {/* Loop-safe spacing: symmetric per-slide padding (flex gap
                            breaks at the loop seam; centre align needs symmetry). */}
                        {/* Edge to edge on desktop: the page gutter sits on
                            the carousel's viewport, so the first card starts on
                            it and the cards run out to the screen's edges. */}
                        <CarouselContent
                            className="max-md:-mx-[0.2691rem]"
                            viewportClassName={cn(
                                HOME_MOBILE_BLEED,
                                'md:px-[1.927vw] lg:px-[max(2.3124rem,calc((100%_-_120rem)/2_+_2.3124rem))]',
                            )}
                        >
                            {cards.map((pro) => (
                                <CarouselItem
                                    key={pro.id}
                                    className="max-md:basis-[calc(38%+0.5382rem)] max-md:px-[0.2691rem] md:basis-[23.903vw] md:px-[0.287vw] lg:basis-[28.6836rem] lg:px-[0.3444rem]"
                                >
                                    <RoccoLink
                                        to={pro.link}
                                        className="group block w-full"
                                    >
                                        <div className="bg-neutral-200 relative w-full overflow-hidden rounded-tile max-md:aspect-[291/400] md:aspect-[446/550]">
                                            <img
                                                src={pro.image}
                                                alt={pro.title}
                                                // Black and white on desktop, into
                                                // colour on hover.
                                                className="absolute inset-0 size-full max-w-none object-cover md:grayscale md:transition-[filter] md:duration-300 md:group-hover:grayscale-0"
                                                loading="lazy"
                                            />
                                            {pro.logo && (
                                                <span
                                                    className="absolute top-[5.65%] left-[6.9%] flex items-center justify-center overflow-hidden rounded-full p-1 max-md:size-[2.7427rem] md:size-[82px]"
                                                    style={{
                                                        backgroundColor:
                                                            pro.logo.background,
                                                    }}
                                                >
                                                    <Image
                                                        src={pro.logo.src}
                                                        alt=""
                                                        size="professionalLogo"
                                                    />
                                                </span>
                                            )}
                                        </div>
                                        <p className="m-0 font-sans leading-[1.5] text-[#1f1f21] max-md:pt-3 max-md:text-[0.6727rem] md:pt-[0.6vw] md:text-[0.78vw] lg:pt-[0.72rem] lg:text-[0.936rem]">
                                            {pro.title}
                                        </p>
                                        {pro.category && (
                                            <p className="m-0 font-sans leading-[1.5] text-[#707070] max-md:text-[0.6727rem] md:text-[0.78vw] md:text-[#7E7C78] lg:text-[0.936rem]">
                                                {pro.category}
                                            </p>
                                        )}
                                    </RoccoLink>
                                </CarouselItem>
                            ))}
                            {cards.length > 0 && (
                                <CarouselBrowseMoreTile
                                    to="/professionals"
                                    noun="Professionals"
                                    theme="light"
                                    itemClassName="max-md:basis-[calc(38%+0.5382rem)] max-md:px-[0.2691rem] md:basis-[23.903vw] lg:basis-[28.6836rem] md:px-[0.287vw] lg:px-[0.3444rem]"
                                    boxClassName="bg-neutral-200 max-md:aspect-[291/400] md:aspect-[446/550]"
                                />
                            )}
                        </CarouselContent>
                        {islandPagination ? null : <ProfessionalsPagination />}
                    </Carousel>
                </div>
            </ScrollReveal>

            {/* Desktop-only: the Get Matched modal has no mobile design yet and
                its DialogContent is `hidden` below `md`, so the trigger and modal
                are kept off mobile to avoid opening an invisible modal. */}
            {getMatchedEnabled && (
                <ScrollReveal className="hidden flex-col items-start md:flex md:gap-[1.93vw] md:px-[3.125vw] md:pt-[4vw] md:pb-[1.5vw] lg:gap-[2.316rem] lg:px-[max(3.75rem,calc((100%_-_120rem)/2_+_3.75rem))] lg:pt-[4.8rem] lg:pb-[1.8rem]">
                    <p
                        ref={promptRef}
                        className="m-0 font-sans leading-[1.3] font-medium text-[#181818] md:w-[30.57vw] md:text-[0.9765vw] md:tracking-[-0.0292vw] lg:w-[36.684rem] lg:text-[1.1718rem] lg:tracking-[-0.0351rem]"
                    >
                        Don’t know where to start?
                        <br />
                        Answer a few questions and get matched with trusted
                        industry professionals.
                    </p>
                    <button
                        type="button"
                        onClick={openGetMatchedModal}
                        className={button('secondary', 'l', 'w-fit')}
                    >
                        Get Matched
                    </button>
                    <GetMatchedHost
                        enquiryType="professional"
                        styleImages={getMatchedStyleImages}
                    />
                </ScrollReveal>
            )}
        </section>
    );
};

/**
 * The homepage's `revamp_2026` design of this section, or its previous one
 * while the flag is off.
 */
export const DiscoverProfessionalsSection = (
    props: ComponentProps<typeof DiscoverProfessionalsSectionRevamp> &
        ComponentProps<typeof DiscoverProfessionalsSectionLegacy>,
) =>
    useHomePageRevamp() ? (
        <DiscoverProfessionalsSectionRevamp {...props} />
    ) : (
        <DiscoverProfessionalsSectionLegacy {...props} />
    );
